/**
 * In-memory test data — mirrors expected backend responses.
 * Backend team: implement endpoints documented in docs/BACKEND_API.md
 */

const LEGENDS_BY_TYPE = {
  NDVI: {
    title: "شاخص تراکم پوشش گیاهی (NDVI)",
    stops: [
      { min: -0.2, max: 0.2, color: "#d73027" },
      { min: 0.2, max: 0.4, color: "#fee08b" },
      { min: 0.4, max: 0.6, color: "#d9ef8b" },
      { min: 0.6, max: 0.8, color: "#91cf60" },
      { min: 0.8, max: 1.0, color: "#1a9850" },
    ],
  },
  NDWI: {
    title: "شاخص رطوبت سطح آب (NDWI)",
    stops: [
      { min: -1, max: 0, color: "#d73027" },
      { min: 0, max: 0.2, color: "#fee08b" },
      { min: 0.2, max: 0.4, color: "#d9ef8b" },
      { min: 0.4, max: 0.6, color: "#91cf60" },
      { min: 0.6, max: 1.0, color: "#1a9850" },
    ],
  },
  LNC: {
    title: "شاخص درصد نیتروژن برگ",
    stops: [
      { min: 0, max: 1, color: "#fff7bc" },
      { min: 1, max: 2, color: "#fec44f" },
      { min: 2, max: 3, color: "#d95f0e" },
      { min: 3, max: 5, color: "#7f2704" },
    ],
  },
  LWC: {
    title: "شاخص درصد رطوبت برگ",
    stops: [
      { min: 0, max: 25, color: "#f7fcb9" },
      { min: 25, max: 50, color: "#addd8e" },
      { min: 50, max: 75, color: "#31a354" },
      { min: 75, max: 100, color: "#006837" },
    ],
  },
};

const PRODUCTS_BY_TYPE = {
  NDVI: [
    {
      id: 101,
      product_type: "NDVI",
      name: "NDVI 1403-09-15",
      date: "2024-12-05",
      min_value: -1,
      max_value: 1,
    },
  ],
  NDWI: [{ id: 111, product_type: "NDWI", name: "NDWI latest", date: "2024-12-01", min_value: -1, max_value: 1 }],
  LNC: [{ id: 201, product_type: "LNC", name: "LNC latest", date: "2024-12-01", min_value: 0, max_value: 5 }],
  LWC: [{ id: 301, product_type: "LWC", name: "LWC latest", date: "2024-12-01", min_value: 0, max_value: 100 }],
  CH: [{ id: 401, product_type: "CH", name: "CH latest", date: "2024-11-20", min_value: 0, max_value: 10 }],
  CWSI: [{ id: 501, product_type: "CWSI", name: "CWSI latest", date: "2024-11-20", min_value: 0, max_value: 1 }],
  ETa: [{ id: 601, product_type: "ETa", name: "ETa latest", date: "2024-11-20", min_value: 0, max_value: 10 }],
  IWS: [{ id: 701, product_type: "IWS", name: "IWS latest", date: "2024-11-20", min_value: 0, max_value: 1 }],
  SM: [{ id: 801, product_type: "SM", name: "SM latest", date: "2024-11-20", min_value: 0, max_value: 100 }],
  YIELD: [{ id: 901, product_type: "YIELD", name: "Yield latest", date: "2024-10-01", min_value: 0, max_value: 100 }],
};

const defaultLegend = (productType) =>
  LEGENDS_BY_TYPE[productType] || {
    title: productType,
    stops: [
      { min: 0, max: 0.25, color: "#fee5d9" },
      { min: 0.25, max: 0.5, color: "#fcae91" },
      { min: 0.5, max: 0.75, color: "#fb6a4a" },
      { min: 0.75, max: 1, color: "#a50f15" },
    ],
  };

const generateSeries = (productType, count = 12) => {
  const cfg = PRODUCTS_BY_TYPE[productType]?.[0];
  const min = cfg?.min_value ?? 0;
  const max = cfg?.max_value ?? 1;
  const span = max - min;
  const labels = [
    "1402/01", "1402/02", "1402/03", "1402/04", "1402/05", "1402/06",
    "1402/07", "1402/08", "1402/09", "1402/10", "1402/11", "1402/12",
  ];
  return labels.slice(0, count).map((label, i) => ({
    product_id: cfg?.id,
    date: label,
    value: Number((min + span * (0.35 + 0.05 * i + Math.random() * 0.15)).toFixed(3)),
  }));
};

export const mockApi = {
  getProductList(productType) {
    return PRODUCTS_BY_TYPE[productType] || [
      {
        id: 999,
        product_type: productType,
        name: `${productType} (mock)`,
        date: "2024-12-01",
        min_value: 0,
        max_value: 1,
      },
    ];
  },

  getProductMetadata(productId, productType) {
    const list = PRODUCTS_BY_TYPE[productType] || [];
    const product = list.find((p) => p.id === productId) || list[0];
    return {
      product_id: productId,
      product_type: productType,
      min_value: product?.min_value ?? 0,
      max_value: product?.max_value ?? 1,
      legend: defaultLegend(productType),
      visualization: {
        opacity: 0.85,
        palette: "sequential",
      },
    };
  },

  getLegend(productType) {
    return defaultLegend(productType);
  },

  getSpatialInit() {
    return {
      keshte: ["هفت تپه", "امام خمینی (ره)", "امیر کبیر"],
      croptype: ["نیشکر", "گندم", "ذرت", "کلزا"],
      types: ["farm", "block", "office", "management"],
    };
  },

  getSpatialOptions(keshte, croptype) {
    return {
      variety: ["CP57", "CP69", "IRC"],
      age: ["پلنت", "راتون 1", "راتون 2"],
      status: ["آیش", "داشت"],
    };
  },

  getSpatialUnits({ keshte, type, croptype, variety, age, status }) {
    const units = {
      farm: ["D26-20-1", "D24-13-1", "D28-14-2"],
      block: ["B01", "B02", "B03"],
      office: ["اداره اول", "اداره دوم"],
      management: ["مدیریت مرکزی", "مدیریت شرقی"],
    };
    return { units: units[type] || [] };
  },

  getTimeSeries({ productType, productId, spatial, dateFrom, dateTo }) {
    const mode = spatial?.mode || "point";
    if (mode === "attribute" && !spatial.filter?.keshte) {
      return {
        __error: { status: 400, message: "فیلتر مکانی ناقص است", body: {} },
      };
    }
    if (mode === "polygon" && (!spatial.coordinates || spatial.coordinates.length < 3)) {
      return {
        __error: { status: 400, message: "محدوده چندضلعی نامعتبر است", body: {} },
      };
    }
    return {
      product_id: productId,
      product_type: productType,
      spatial_mode: mode,
      date_from: dateFrom || null,
      date_to: dateTo || null,
      time_series: generateSeries(productType),
    };
  },
};
